import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles, useTheme } from '@material-ui/core';

import { getI18n } from "../../../utils/intl";
import { RespCodeList } from '../../../constants/customReport';

import styles from './styles';

const filterLabelMap = {
  apiId: 'Api(s)',
  apiKeyId: 'API Key(s)',
  appId: 'Application(s)',
  orgId: 'Organization(s)',
  gatewayServerId: 'Proxy(s)',
  org: 'Organizations',
  api: 'APIs',
  app: 'Applications',
  gateway: 'Gateways',
  respCode: 'Response Code(s)',
  uri: 'URI(s)',
};

const filterLength = (labels) => {
  const filteredLabels = labels.filter(
    label => (label === 0) || (label.name && label.name.trim() !== ''));
  return filteredLabels.length;
}

const FilterLabel = (props) => {
  const {
    handleClick, apiList, appList, orgList, gatewayList,
    apiKeyDetailsMap, filterLabels: filterLabelsProp,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [filterLabels, setFilterLabels] = useState([]);
  const theme = useTheme();

  const filterLabelsPropRef = useRef(filterLabelsProp);
  useEffect(() => {
    if (filterLabelsPropRef.current !== filterLabelsProp) {
      let numberOfRows = 1;
      let numberOfColumns = 0;
      filterLabelsProp.forEach(label => {
        if (label.type === 'in') {
          if (!label.isGrouped) {
            numberOfRows *= _.keys(label.values).length;
          }
          numberOfColumns += 1;
        } else if (label.type === 'top') {
          numberOfRows *= label.limit;
          numberOfColumns += 1;
        } else if (label.type === 'range') {
          numberOfRows *= label.ranges.length;
          numberOfColumns += 1;
        } else if (label.type === 'or') {
          numberOfColumns += 1;
        } else if (label.type === 'regex') {
          if (!label.isGrouped) {
            numberOfRows *= label.values.length;
          }
          numberOfColumns += 1;
        }
      });
      const filterLabels = filterLabelsProp.filter(
        label => label.type !== 'default',
      );
      buildFilterLabel(numberOfRows, numberOfColumns, filterLabels);
    }
    filterLabelsPropRef.current = filterLabelsProp;
  }, [filterLabelsProp]);
  
  const getDimensionName = (
    id,
    value,
    apiList,
    apiKeyDetailsMap,
    appList,
    orgList,
    gatewayList,
    type,
  ) => {
    let dimensionNames;
    let formattedNames;
    const deletedEntityText = `${value} (deleted)`;
    switch (id) {
      case 1:
        dimensionNames = apiList[value] || deletedEntityText;
        break;
      case 2:
        dimensionNames = appList[value] || deletedEntityText;
        break;
      case 3:
        dimensionNames = gatewayList[value] || deletedEntityText;
        break;
      case 4:
        dimensionNames = orgList[value] || deletedEntityText;
        break;
      case 5:
        dimensionNames =
          type === 'or' || type === 'range'
            ? intl.getI18nMessage(RespCodeList[value].labelKey)
            : value;
        break;
      case 6:
        dimensionNames = value.name;
        break;
      case 7:
        {
          const { name, appName, orgName } = apiKeyDetailsMap[value] || {};
          const textColor = theme.color.bodyText;
          const linkColor = theme.color.accent;
          dimensionNames = name || deletedEntityText;
          if (name && orgName && appName) {
            formattedNames = `<span style="color:${linkColor}">${name} </span>
              <span style="color:${textColor}"> (${orgName} › ${appName}) <span>`;
          }
        }
        break;
      default:
    }
    return {
      name: dimensionNames,
      formattedName: formattedNames,
    };
  };
  
  const buildFilterLabel = (rows, columns, labels) => {
    const numberOfElements = labels.map(label => {
      let count = 0;
      if (label.type === 'in') {
        count = label.isGrouped ? 1 : _.keys(label.values).length;
      } else if (label.type === 'top') {
        count = label.limit;
      }
      if (label.type === 'or') {
        count = label.isGrouped ? 1 : label.fields.length;
      } else if (label.type === 'regex') {
        count = label.isGrouped ? 1 : label.values.length;
      } else if (label.type === 'range') {
        count = label.ranges.length;
      }
      return count;
    });
    const modulusFactor = [];
    for (let i = 0; i < numberOfElements.length; i++) {
      let factor = 1;
      for (let k = i + 1; k < numberOfElements.length; k++) {
        factor *= numberOfElements[k];
      }
      modulusFactor[i] = factor;
    }
    const filterLabelsMatrix = Array(rows)
      .fill()
      .map(() => Array(columns).fill(0));
    labels.forEach((label, idx) => {
      let id = 0;
      let fieldsId = 0;
      let regexId = 0;
      let rank = 1;
      for (let m = 0; m < rows; m++) {
        for (let n = 0; n < columns; n++) {
          if (m % modulusFactor[idx] === 0 && n === idx) {
            let key;
            if (label.type === 'in' && !label.isGrouped) {
              key = _.keys(label.values)[id++];
            } else if (label.type === 'top' && rank > label.limit) {
              rank = 1;
            } else if (label.type === 'or' && !label.isGrouped) {
              key = `${label.fields[fieldsId].lower}-${label.fields[fieldsId].upper}`;
              if (fieldsId === label.fields.length - 1) {
                fieldsId = 0;
              } else {
                fieldsId++;
              }
            } else if (label.type === 'range') {
              key = `${label.ranges[fieldsId].from}-${label.ranges[fieldsId].to}`;
              if (fieldsId === label.ranges.length - 1) {
                fieldsId = 0;
              } else {
                fieldsId++;
              }
            } else if (label.type === 'regex' && !label.isGrouped) {
              key = label.values[regexId++];
            }
            if (!key) {
              if (label.type === 'in') {
                id = 0;
                key = _.keys(label.values)[id++];
              } else if (label.type === 'regex') {
                regexId = 0;
                key = label.values[regexId++];
              }
            }
            if (apiList && apiKeyDetailsMap && appList && orgList) {
              let name = '';
              let formattedName = '';
              let count = 0;
              if (label.type === 'in' || label.type === 'or' || label.type === 'range') {
                if (!label.isGrouped) {
                  ({ name, formattedName } = getDimensionName(
                    label.dimensionId,
                    key,
                    apiList,
                    apiKeyDetailsMap,
                    appList,
                    orgList,
                    gatewayList,
                    label.type,
                  ));
                } else {
                  name = filterLabelMap[label.dimension];
                  count =
                    label.type === 'or'
                      ? label.fields.length
                      : _.keys(label.values).length;
                }
              } else if (label.type === 'top') {
                name = `Top ${rank++} ${filterLabelMap[label.dimension]}`;
              } else if (label.type === 'regex') {
                if (!label.isGrouped) {
                  ({ name, formattedName } = getDimensionName(
                    label.dimensionId,
                    key,
                    apiList,
                    apiKeyDetailsMap,
                    appList,
                    orgList,
                    gatewayList,
                    label.type,
                  ));
                } else {
                  name = filterLabelMap[label.dimension];
                  count = label.values.length;
                }
              }
              filterLabelsMatrix[m][n] = {
                dimensionId: label.dimensionId,
                name,
                formattedName,
                count,
                isGrouped: label.isGrouped,
              };
            }
          }
        }
      }
    });
    setFilterLabels(filterLabelsMatrix);
  };

  const renderName = (name, formattedName) => {
    if (formattedName) {
      // eslint-disable-next-line react/no-danger
      return <span dangerouslySetInnerHTML={{ __html: formattedName }} />;
    }
    return name;
  }
  
  let rows = [];
  let entry = [];
  rows = filterLabels.map((label, labelIndex) => {
    entry = label.map((element, colIdx) => (
      <td className={classes.tableData} key={colIdx}>
        {element !== 0 && element.name && element.name.trim() !== '' ? (
          <div
            className={clsx(
              classes.tableElement,
              colIdx === 0 ? classes.borderLeft : null,
              colIdx === filterLength(label) - 1 ? classes.borderRight : null,
            )}
            onClick={() => handleClick(element.dimensionId)}
          >
            {colIdx !== 0 && <div className={classes.arrow} />}
            {renderName(element.name, element.formattedName)}
            {element.isGrouped && (
              <div className={classes.metricCountContainer}>
                {element.count}
              </div>
            )}
          </div>
        ) : null}
      </td>
    ));
    return <tr key={labelIndex}>{entry}</tr>;
  });

  if (!entry.length) {
    rows = (
      <tr>
        <td className={classes.tableData}>
          <div
            className={clsx(
              classes.tableElement,
              classes.borderLeft,
              classes.borderRight,
            )}
            onClick={() => handleClick('All Data')}
          >
            {intl.getI18nMessage('label.analytics.customReport.dashboard.filter.allData.label')}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <div className={classes.metricTypeBox}>
      <table className={classes.table}>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

FilterLabel.propTypes = {
  handleClick: PropTypes.func,
  apiList: PropTypes.object,
  apiKeyDetailsMap: PropTypes.object,
  appList: PropTypes.object,
  orgList: PropTypes.object,
  gatewayList: PropTypes.object,
  filterLabels: PropTypes.array,
};

export default FilterLabel;